/**
 * FDM Exceedances Tab Component
 * Shows exceedance events with filtering
 */

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertTriangle, CheckCircle2, Clock, RefreshCw } from 'lucide-react';
import { getSeverityColor, formatCategory } from './fdmUtils';

export default function FDMExceedancesTab({ 
  exceedances, 
  exceedancesTotal,
  exceedanceFilters,
  setExceedanceFilters,
  categories,
  onExceedanceClick
}) {
  return (
    <div className="space-y-4 mt-6">
      {/* Filters */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="space-y-1.5">
              <Label className="text-slate-400 text-xs">Category</Label>
              <Select 
                value={exceedanceFilters.category || 'all'} 
                onValueChange={(v) => setExceedanceFilters({...exceedanceFilters, category: v === 'all' ? undefined : v})}
              >
                <SelectTrigger className="w-[180px] bg-slate-900/50">
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat.value || cat.id} value={cat.value || cat.id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-slate-400 text-xs">Severity</Label>
              <Select 
                value={exceedanceFilters.severity || 'all'} 
                onValueChange={(v) => setExceedanceFilters({...exceedanceFilters, severity: v === 'all' ? undefined : v})}
              >
                <SelectTrigger className="w-[160px] bg-slate-900/50">
                  <SelectValue placeholder="All Severities" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Severities</SelectItem>
                  <SelectItem value="level_1">Level 1 - Minor</SelectItem>
                  <SelectItem value="level_2">Level 2 - Moderate</SelectItem>
                  <SelectItem value="level_3">Level 3 - Serious</SelectItem>
                  <SelectItem value="level_4">Level 4 - Severe</SelectItem>
                  <SelectItem value="level_5">Level 5 - Critical</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-slate-400 text-xs">Status</Label>
              <Select 
                value={exceedanceFilters.status || 'all'} 
                onValueChange={(v) => setExceedanceFilters({...exceedanceFilters, status: v === 'all' ? undefined : v})}
              >
                <SelectTrigger className="w-[140px] bg-slate-900/50">
                  <SelectValue placeholder="Any Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Any Status</SelectItem>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="under_review">Under Review</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                  <SelectItem value="linked_to_incident">Linked</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setExceedanceFilters({})}>
              <RefreshCw className="w-4 h-4 mr-1" /> Clear
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Exceedances List */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-lg text-slate-100">Exceedance Events</CardTitle>
          <CardDescription>{exceedancesTotal} total events</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {exceedances.map((exc) => (
              <div 
                key={exc.id} 
                className="flex items-center justify-between p-4 rounded-lg bg-slate-700/30 hover:bg-slate-700/50 transition-colors cursor-pointer" 
                onClick={() => onExceedanceClick(exc)}
              >
                <div className="flex items-center gap-4">
                  <div className={`p-2 rounded-lg ${exc.severity?.includes('5') || exc.severity?.includes('4') ? 'bg-red-500/20' : exc.severity?.includes('3') ? 'bg-orange-500/20' : 'bg-yellow-500/20'}`}>
                    <AlertTriangle className={`w-5 h-5 ${exc.severity?.includes('5') || exc.severity?.includes('4') ? 'text-red-400' : exc.severity?.includes('3') ? 'text-orange-400' : 'text-yellow-400'}`} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-slate-200">{formatCategory(exc.category)}</span>
                      <Badge className={getSeverityColor(exc.severity)}>{exc.severity?.replace('_', ' ')}</Badge>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-slate-400 mt-1">
                      <span>{exc.flight_info?.flight_number}</span>
                      <span className="text-slate-600">•</span>
                      <span>{exc.flight_info?.aircraft_registration}</span>
                      <span className="text-slate-600">•</span>
                      <span className="capitalize">{exc.phase?.replace('_', ' ')}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right text-sm">
                    <p className="text-slate-300">{exc.parameter_value} {exc.unit}</p>
                    <p className="text-slate-500">Threshold: {exc.threshold_value}</p>
                  </div>
                  {exc.reviewed ? (
                    <CheckCircle2 className="w-5 h-5 text-green-400" />
                  ) : (
                    <Clock className="w-5 h-5 text-yellow-400" />
                  )}
                </div>
              </div>
            ))}
            {exceedances.length === 0 && (
              <div className="text-center py-12">
                <AlertTriangle className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                <p className="text-slate-400">No exceedances found</p>
                <p className="text-slate-500 text-sm mt-1">Exceedances will appear here when detected in flight data</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
